import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { getNCFConfig, updateNCFConfig, getCompanyConfig, updateCompanyConfig, uploadCompanyLogo, deleteCompanyLogo, getPrintConfig, updatePrintConfig, sendTailoringReceipt, updateMyPreferences, getNotificationConfig, updateNotificationConfig, changeMyPassword, getCategories, createCategory, updateCategory, deleteCategory, bulkPriceAdjust } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, Building2, Printer, FileText, Mail, User, Bell, Tag, Pencil, Trash2, Plus, Check, X, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

const Settings = () => {
  const [ncfConfig, setNcfConfig] = useState([]);
  const [companyConfig, setCompanyConfig] = useState({
    name: '',
    rnc: '',
    phone: '',
    address: '',
    email: '',
    website: '',
    slogan: '',
    card_surcharge_pct: 0,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const { user, updateUser } = useAuth();
  const safeUpdateUser = updateUser || (() => {});
  const isAdmin = user?.role === 'admin';
  const [prefConfig, setPrefConfig] = useState({
    receipt_preference: user?.receipt_preference || 'email',
    receipt_timer: user?.receipt_timer || 15,
  });
  const [savingPref, setSavingPref] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [notifConfig, setNotifConfig] = useState({
    notifications_enabled: true,
    admin_email: '',
    taller_email: '',
    notify_admin_pre_produccion: true,
    notify_taller_pre_produccion: true,
    notify_seller_listo: true,
    notify_client_listo: true,
    notify_cash_open: true,
    notify_cash_close: true,
  });
  const [savingNotif, setSavingNotif] = useState(false);
  const [printConfig, setPrintConfig] = useState({
    paper_width: '80',
    show_logo: false,
    show_customer_info: true,
    show_notes: true,
    show_cash_savings: true,
    footer_message: '¡Gracias por su compra!',
    cash_register_print_format: 'letter',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState(new Set());
  const toggleSection = (key) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const [priceAdjustPct, setPriceAdjustPct] = useState('');
  const [priceAdjustDir, setPriceAdjustDir] = useState('up');
  const [priceAdjustConfirm, setPriceAdjustConfirm] = useState(false);
  const [applyingPriceAdjust, setApplyingPriceAdjust] = useState(false);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // { id, name }
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    loadAllConfig();
  }, []);

  const loadAllConfig = async () => {
    try {
      const [ncf, company, print, notif, cats] = await Promise.all([
        getNCFConfig().catch(() => null),
        getCompanyConfig().catch(() => null),
        getPrintConfig().catch(() => null),
        getNotificationConfig().catch(() => null),
        getCategories().catch(() => []),
      ]);
      if (ncf)    setNcfConfig(ncf);
      if (company) { setCompanyConfig(company); if (company.logo_b64) setLogoPreview(company.logo_b64); }
      if (print)  setPrintConfig(prev => ({ ...prev, ...print }));
      if (notif)  setNotifConfig(notif);
      if (cats)   setCategories(cats);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNCF = async (tipo, config) => {
    setSaving(true);
    try {
      await updateNCFConfig(tipo, config);
      toast.success('Configuración de NCF actualizada');
      loadAllConfig();
    } catch (error) {
      toast.error('Error al actualizar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300_000) { toast.error('La imagen debe pesar menos de 300KB'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const b64 = ev.target.result;
      setLogoPreview(b64);
      setUploadingLogo(true);
      try {
        await uploadCompanyLogo(b64, companyConfig.show_logo ?? true);
        toast.success('Logo guardado');
      } catch { toast.error('Error subiendo logo'); }
      finally { setUploadingLogo(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async () => {
    setUploadingLogo(true);
    try {
      await deleteCompanyLogo();
      setLogoPreview(null);
      toast.success('Logo eliminado');
    } catch { toast.error('Error eliminando logo'); }
    finally { setUploadingLogo(false); }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await updateCompanyConfig(companyConfig);
      toast.success('Datos de la empresa actualizados');
    } catch (error) {
      toast.error('Error al actualizar datos de la empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    const testEmail = prompt("Ingresa un correo para la prueba:");
    if (!testEmail) return;
    setSmtpTesting(true);
    try {
      // Send a test by hitting send-receipt on a dummy call — we just test SMTP config directly
      await updatePrintConfig(printConfig);
      toast.success("Configuración SMTP guardada. Prueba enviando un comprobante real desde un pedido.");
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.error || err.message));
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotif(true);
    try {
      await updateNotificationConfig(notifConfig);
      toast.success('Configuración de notificaciones guardada');
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally {
      setSavingNotif(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Todos los campos son requeridos'); return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres'); return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden'); return;
    }
    setSavingPassword(true);
    try {
      await changeMyPassword(passwordForm);
      toast.success('Contraseña actualizada');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error actualizando contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSavePreferences = async () => {
    setSavingPref(true);
    try {
      const updated = await updateMyPreferences(prefConfig);
      safeUpdateUser(updated);
      toast.success('Preferencias guardadas');
    } catch (err) {
      toast.error('Error guardando preferencias');
    } finally {
      setSavingPref(false);
    }
  };

  const handleSavePrint = async () => {
    setSaving(true);
    try {
      await updatePrintConfig(printConfig);
      toast.success('Configuración de impresión actualizada');
    } catch (error) {
      toast.error('Error al actualizar configuración de impresión');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkPriceAdjust = async () => {
    const pct = parseFloat(priceAdjustPct);
    if (!pct || pct <= 0) { toast.error('Ingresa un porcentaje válido mayor a 0'); return; }
    const finalPct = priceAdjustDir === 'down' ? -pct : pct;
    setApplyingPriceAdjust(true);
    try {
      const res = await bulkPriceAdjust(finalPct);
      toast.success(res.message);
      setPriceAdjustPct('');
      setPriceAdjustConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar precios');
    } finally {
      setApplyingPriceAdjust(false);
    }
  };

  const handleConfigChange = (index, field, value) => {
    const newConfig = [...ncfConfig];
    newConfig[index] = { ...newConfig[index], [field]: value };
    setNcfConfig(newConfig);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Configuración</h1>
      </div>

      <Tabs defaultValue={isAdmin ? "company" : "preferences"} className="w-full">
        <TabsList className={`grid w-full mb-6 ${isAdmin ? 'grid-cols-7' : 'grid-cols-1'}`}>
          {isAdmin && (
            <>
              <TabsTrigger value="company" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Empresa
              </TabsTrigger>
              <TabsTrigger value="print" className="flex items-center gap-2">
                <Printer className="w-4 h-4" />
                Impresión
              </TabsTrigger>
              <TabsTrigger value="ncf" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                NCF
              </TabsTrigger>
              <TabsTrigger value="smtp" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Correo
              </TabsTrigger>
              <TabsTrigger value="categories" className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Categorías
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Mis preferencias
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notificaciones
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab: Empresa */}
        {isAdmin && (
        <TabsContent value="company">
          <Card className="bg-white border border-slate-200 shadow-sm divide-y divide-slate-200">

            {/* Sección: Datos de la Empresa */}
            <div>
              <button
                onClick={() => toggleSection('datos')}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-cyan-500" />
                  <span className="font-semibold text-slate-800" style={{ fontFamily: 'Outfit' }}>Datos de la Empresa</span>
                  <span className="text-xs text-slate-400">Nombre, RNC, dirección, contacto</span>
                </div>
                {openSections.has('datos') ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {openSections.has('datos') && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <p className="text-sm text-blue-900">Estos datos aparecerán en las facturas y recibos impresos.</p>
                  </div>

                  {/* Logo */}
                  <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Logo de la empresa</p>
                        <p className="text-xs text-slate-500">Se mostrará en la parte superior de las facturas. PNG o JPG, máx 300KB.</p>
                      </div>
                      <Switch
                        checked={companyConfig.show_logo ?? false}
                        onCheckedChange={async (v) => {
                          setCompanyConfig({ ...companyConfig, show_logo: v });
                          if (logoPreview) {
                            try { await uploadCompanyLogo(logoPreview, v); }
                            catch { toast.error('Error actualizando preferencia'); }
                          }
                        }}
                        disabled={!logoPreview}
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="h-16 object-contain rounded border border-slate-200 bg-slate-50 p-1" />
                      ) : (
                        <div className="h-16 w-24 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs text-slate-400">Sin logo</div>
                      )}
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 text-sm hover:bg-slate-50 transition-colors">
                          {uploadingLogo ? 'Subiendo...' : logoPreview ? 'Cambiar logo' : 'Subir logo'}
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoChange} disabled={uploadingLogo} />
                        </label>
                        {logoPreview && (
                          <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 justify-start px-3" onClick={handleDeleteLogo} disabled={uploadingLogo}>
                            Eliminar logo
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre de la Empresa *</Label>
                      <Input value={companyConfig.name} onChange={(e) => setCompanyConfig({ ...companyConfig, name: e.target.value })} placeholder="Ej: Batas Médicas RD" data-testid="company-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>RNC</Label>
                      <Input value={companyConfig.rnc} onChange={(e) => setCompanyConfig({ ...companyConfig, rnc: e.target.value })} placeholder="Ej: 123-45678-9" data-testid="company-rnc" />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono *</Label>
                      <Input value={companyConfig.phone} onChange={(e) => setCompanyConfig({ ...companyConfig, phone: e.target.value })} placeholder="Ej: 809-555-1234" data-testid="company-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={companyConfig.email} onChange={(e) => setCompanyConfig({ ...companyConfig, email: e.target.value })} placeholder="Ej: info@empresa.com" data-testid="company-email" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Dirección *</Label>
                      <Input value={companyConfig.address} onChange={(e) => setCompanyConfig({ ...companyConfig, address: e.target.value })} placeholder="Ej: Calle Principal #123, Santo Domingo" data-testid="company-address" />
                    </div>
                    <div className="space-y-2">
                      <Label>Sitio Web</Label>
                      <Input value={companyConfig.website} onChange={(e) => setCompanyConfig({ ...companyConfig, website: e.target.value })} placeholder="Ej: www.empresa.com" data-testid="company-website" />
                    </div>
                    <div className="space-y-2">
                      <Label>Eslogan</Label>
                      <Input value={companyConfig.slogan} onChange={(e) => setCompanyConfig({ ...companyConfig, slogan: e.target.value })} placeholder="Ej: Calidad y confianza" data-testid="company-slogan" />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram</Label>
                      <div className="flex items-center">
                        <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-200 rounded-l-md text-slate-500 text-sm">@</span>
                        <Input value={companyConfig.instagram || ''} onChange={(e) => setCompanyConfig({ ...companyConfig, instagram: e.target.value.replace('@','') })} placeholder="tuempresa" className="rounded-l-none" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Zona Horaria</Label>
                      <Select value={companyConfig.timezone || 'America/Santo_Domingo'} onValueChange={(v) => setCompanyConfig({ ...companyConfig, timezone: v })}>
                        <SelectTrigger data-testid="company-timezone">
                          <SelectValue placeholder="Seleccionar zona horaria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/Santo_Domingo">Santo Domingo (UTC-4)</SelectItem>
                          <SelectItem value="America/Puerto_Rico">Puerto Rico (UTC-4)</SelectItem>
                          <SelectItem value="America/Havana">La Habana (UTC-5/-4)</SelectItem>
                          <SelectItem value="America/New_York">Nueva York (UTC-5/-4)</SelectItem>
                          <SelectItem value="America/Chicago">Chicago (UTC-6/-5)</SelectItem>
                          <SelectItem value="America/Mexico_City">Ciudad de México (UTC-6/-5)</SelectItem>
                          <SelectItem value="America/Costa_Rica">Costa Rica (UTC-6)</SelectItem>
                          <SelectItem value="America/Panama">Panamá (UTC-5)</SelectItem>
                          <SelectItem value="America/Bogota">Bogotá (UTC-5)</SelectItem>
                          <SelectItem value="America/Lima">Lima (UTC-5)</SelectItem>
                          <SelectItem value="America/Caracas">Caracas (UTC-4)</SelectItem>
                          <SelectItem value="America/La_Paz">La Paz (UTC-4)</SelectItem>
                          <SelectItem value="America/Santiago">Santiago (UTC-4/-3)</SelectItem>
                          <SelectItem value="America/Asuncion">Asunción (UTC-4/-3)</SelectItem>
                          <SelectItem value="America/Buenos_Aires">Buenos Aires (UTC-3)</SelectItem>
                          <SelectItem value="America/Montevideo">Montevideo (UTC-3)</SelectItem>
                          <SelectItem value="America/Sao_Paulo">São Paulo (UTC-3/-2)</SelectItem>
                          <SelectItem value="America/Guayaquil">Guayaquil (UTC-5)</SelectItem>
                          <SelectItem value="Europe/Madrid">Madrid (UTC+1/+2)</SelectItem>
                          <SelectItem value="UTC">UTC</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400">Afecta los filtros de fecha en reportes, facturas y dashboard.</p>
                    </div>
                  </div>
                  <Button onClick={handleSaveCompany} disabled={saving} className="w-full bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-company">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Guardando...' : 'Guardar Datos de Empresa'}
                  </Button>
                </div>
              )}
            </div>

            {/* Sección: Descuento por pago en efectivo */}
            <div>
              <button
                onClick={() => toggleSection('descuento')}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  <span className="font-semibold text-slate-800" style={{ fontFamily: 'Outfit' }}>Recargo por pago con tarjeta</span>
                  <span className="text-xs text-slate-400">{companyConfig.card_surcharge_pct > 0 ? `${companyConfig.card_surcharge_pct}% configurado` : 'No configurado'}</span>
                </div>
                {openSections.has('descuento') ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {openSections.has('descuento') && (
                <div className="px-5 pb-5 space-y-4">
                  <p className="text-sm text-slate-500">El precio base del producto es el precio en efectivo. Al pagar con tarjeta se aplica este recargo adicional sobre el subtotal.</p>
                  <div className="max-w-xs space-y-2">
                    <Label>Porcentaje de recargo con tarjeta</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number" min="0" max="100" step="0.1" placeholder="0"
                        value={companyConfig.card_surcharge_pct ?? 0}
                        onChange={(e) => setCompanyConfig({ ...companyConfig, card_surcharge_pct: parseFloat(e.target.value) || 0 })}
                      />
                      <span className="text-slate-500 text-sm font-medium shrink-0">%</span>
                    </div>
                  </div>
                  <Button onClick={handleSaveCompany} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              )}
            </div>

            {/* Sección: Ajuste masivo de precios */}
            <div>
              <button
                onClick={() => toggleSection('ajuste')}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold text-slate-800" style={{ fontFamily: 'Outfit' }}>Ajuste masivo de precios</span>
                  <span className="text-xs text-slate-400">Aumentar o disminuir todos los precios en %</span>
                </div>
                {openSections.has('ajuste') ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {openSections.has('ajuste') && (
                <div className="px-5 pb-5 space-y-4">
                  <p className="text-sm text-slate-500">Modifica el precio de todos los productos en un porcentaje. <strong>Esta acción es irreversible.</strong> Los precios resultantes se redondean hacia arriba al múltiplo de 10 más cercano (ej. $1,063 → $1,070).</p>

                  <div className="space-y-2">
                    <Label>Acción</Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setPriceAdjustDir('up'); setPriceAdjustConfirm(false); }}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${priceAdjustDir === 'up' ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      >
                        ↑ Aumentar
                      </button>
                      <button
                        onClick={() => { setPriceAdjustDir('down'); setPriceAdjustConfirm(false); }}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${priceAdjustDir === 'down' ? 'bg-rose-500 text-white border-rose-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      >
                        ↓ Disminuir
                      </button>
                    </div>
                  </div>

                  <div className="max-w-xs space-y-2">
                    <Label>Porcentaje</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number" min="0.1" max="100" step="0.1" placeholder="Ej: 6"
                        value={priceAdjustPct}
                        onChange={(e) => { setPriceAdjustPct(e.target.value); setPriceAdjustConfirm(false); }}
                      />
                      <span className="text-slate-500 text-sm font-medium shrink-0">%</span>
                    </div>
                  </div>

                  {!priceAdjustConfirm ? (
                    <Button
                      variant="outline"
                      disabled={!priceAdjustPct || parseFloat(priceAdjustPct) <= 0}
                      onClick={() => setPriceAdjustConfirm(true)}
                      className={priceAdjustDir === 'up' ? 'border-emerald-400 text-emerald-700 hover:bg-emerald-50' : 'border-rose-400 text-rose-700 hover:bg-rose-50'}
                    >
                      {priceAdjustDir === 'up' ? '↑ Aumentar todos los precios' : '↓ Disminuir todos los precios'}
                    </Button>
                  ) : (
                    <div className={`border rounded-lg p-3 space-y-2 ${priceAdjustDir === 'up' ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'}`}>
                      <p className={`text-sm font-medium ${priceAdjustDir === 'up' ? 'text-emerald-800' : 'text-rose-800'}`}>
                        ¿Confirmas {priceAdjustDir === 'up' ? 'subir' : 'bajar'} todos los precios un {priceAdjustPct}%? Esta acción no se puede deshacer.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleBulkPriceAdjust}
                          disabled={applyingPriceAdjust}
                          className={priceAdjustDir === 'up' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-rose-500 hover:bg-rose-600 text-white'}
                        >
                          {applyingPriceAdjust ? 'Aplicando...' : 'Sí, aplicar'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setPriceAdjustConfirm(false)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

          </Card>
        </TabsContent>
        )}

        {/* Tab: Configuración de Impresión */}
        {isAdmin && (
        <TabsContent value="print">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                <Printer className="w-5 h-5 mr-2 text-cyan-500" />
                Configuración de Impresión
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  Configure el formato de impresión para su impresora térmica.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Ancho del Papel</Label>
                  <Select
                    value={printConfig.paper_width}
                    onValueChange={(value) => setPrintConfig({ ...printConfig, paper_width: value })}
                  >
                    <SelectTrigger data-testid="paper-width">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="80">80mm (Estándar)</SelectItem>
                      <SelectItem value="58">58mm (Compacto)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    80mm es el tamaño estándar para la mayoría de impresoras térmicas.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Formato de reporte de caja</Label>
                  <Select
                    value={printConfig.cash_register_print_format || 'letter'}
                    onValueChange={(value) => setPrintConfig({ ...printConfig, cash_register_print_format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="letter">Carta (8½ × 11")</SelectItem>
                      <SelectItem value="thermal">Térmica (80mm)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Formato usado al imprimir el cierre de turno.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mostrar Info del Cliente</Label>
                      <p className="text-xs text-slate-500">Incluir nombre y teléfono del cliente</p>
                    </div>
                    <Switch
                      checked={printConfig.show_customer_info}
                      onCheckedChange={(checked) => setPrintConfig({ ...printConfig, show_customer_info: checked })}
                      data-testid="show-customer-info"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mostrar Notas</Label>
                      <p className="text-xs text-slate-500">Incluir notas de la factura</p>
                    </div>
                    <Switch
                      checked={printConfig.show_notes}
                      onCheckedChange={(checked) => setPrintConfig({ ...printConfig, show_notes: checked })}
                      data-testid="show-notes"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mostrar ahorro por efectivo</Label>
                      <p className="text-xs text-slate-500">Indica en la factura cuánto ahorró el cliente al pagar en efectivo</p>
                    </div>
                    <Switch
                      checked={printConfig.show_cash_savings ?? true}
                      onCheckedChange={(checked) => setPrintConfig({ ...printConfig, show_cash_savings: checked })}
                    />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Mensaje de Pie de Página</Label>
                  <Input
                    value={printConfig.footer_message}
                    onChange={(e) => setPrintConfig({ ...printConfig, footer_message: e.target.value })}
                    placeholder="Ej: ¡Gracias por su compra!"
                    data-testid="footer-message"
                  />
                </div>
              </div>

              {/* Vista previa del recibo */}
              <div className="border border-slate-300 rounded-lg p-4 bg-white">
                <p className="text-sm font-medium text-slate-600 mb-3">Vista Previa del Recibo:</p>
                <div 
                  className="bg-white border border-dashed border-slate-300 p-4 mx-auto font-mono text-xs"
                  style={{ 
                    width: printConfig.paper_width === '80' ? '300px' : '220px',
                    fontSize: printConfig.paper_width === '80' ? '12px' : '10px'
                  }}
                >
                  <div className="text-center border-b border-slate-300 pb-2 mb-2">
                    {logoPreview && companyConfig.show_logo && (
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="mx-auto mb-1 object-contain"
                        style={{ maxHeight: '48px', maxWidth: '100%' }}
                      />
                    )}
                    <p className="font-bold">{companyConfig.name || 'NOMBRE EMPRESA'}</p>
                    {companyConfig.rnc && <p>RNC: {companyConfig.rnc}</p>}
                    <p>{companyConfig.address || 'Dirección'}</p>
                    <p>Tel: {companyConfig.phone || '000-000-0000'}</p>
                  </div>
                  <div className="border-b border-slate-300 pb-2 mb-2">
                    <p>FACTURA: INV-000001</p>
                    <p>NCF: B0100000001</p>
                    <p>Fecha: {new Date().toLocaleDateString('es-DO')}</p>
                    {printConfig.show_customer_info && (
                      <p>Cliente: Cliente Ejemplo</p>
                    )}
                  </div>
                  <div className="border-b border-slate-300 pb-2 mb-2">
                    <p>1x Producto Ejemplo</p>
                    <p className="text-right">$100.00</p>
                  </div>
                  <div className="border-b border-slate-300 pb-2 mb-2">
                    <p>Subtotal: $100.00</p>
                    <p>ITBIS 18%: $18.00</p>
                    <p className="font-bold">TOTAL: $118.00</p>
                    {(printConfig.show_cash_savings ?? true) && (
                      <p className="text-slate-500">Ahorro por efectivo: $0.00</p>
                    )}
                  </div>
                  {printConfig.footer_message && (
                    <p className="text-center mt-2">{printConfig.footer_message}</p>
                  )}
                  {(companyConfig.instagram || companyConfig.website) && (
                    <div className="text-center mt-1">
                      {companyConfig.instagram && <p>@{companyConfig.instagram}</p>}
                      {companyConfig.website && <p>{companyConfig.website}</p>}
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSavePrint}
                disabled={saving}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="save-print"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración de Impresión'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Tab: Configuración de NCF */}
        {isAdmin && (
        <TabsContent value="ncf">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                <SettingsIcon className="w-5 h-5 mr-2 text-cyan-500" />
                Configuración de NCF
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Importante:</strong> Configure aquí los rangos de Números de Comprobante Fiscal (NCF) asignados por la DGII.
                  El sistema controlará automáticamente la numeración secuencial.
                </p>
              </div>

              {ncfConfig.map((config, index) => (
                <Card key={config.tipo} className="border border-slate-200" data-testid={`ncf-config-${config.tipo}`}>
                  <CardHeader>
                    <CardTitle className="text-lg capitalize" style={{ fontFamily: 'Outfit' }}>
                      {config.tipo.replace('_', ' ')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Prefijo</Label>
                        <Input
                          value={config.prefijo}
                          onChange={(e) => handleConfigChange(index, 'prefijo', e.target.value)}
                          className="mono-font"
                          data-testid={`ncf-prefix-${config.tipo}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Secuencia Actual</Label>
                        <Input
                          type="number"
                          value={config.secuencia_actual}
                          onChange={(e) => handleConfigChange(index, 'secuencia_actual', parseInt(e.target.value))}
                          className="mono-font"
                          data-testid={`ncf-current-${config.tipo}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Secuencia Inicio</Label>
                        <Input
                          type="number"
                          value={config.secuencia_inicio}
                          onChange={(e) => handleConfigChange(index, 'secuencia_inicio', parseInt(e.target.value))}
                          className="mono-font"
                          data-testid={`ncf-start-${config.tipo}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Secuencia Fin</Label>
                        <Input
                          type="number"
                          value={config.secuencia_fin}
                          onChange={(e) => handleConfigChange(index, 'secuencia_fin', parseInt(e.target.value))}
                          className="mono-font"
                          data-testid={`ncf-end-${config.tipo}`}
                        />
                      </div>
                    </div>

                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-sm text-slate-600 mb-1">Vista Previa del NCF:</p>
                      <p className="text-lg font-bold mono-font text-cyan-600">
                        {config.prefijo}{String(config.secuencia_actual).padStart(8, '0')}
                      </p>
                      <p className="text-xs text-slate-500 mt-2">
                        Disponibles: {config.secuencia_fin - config.secuencia_actual + 1} NCFs
                      </p>
                    </div>

                    <Button
                      onClick={() => handleSaveNCF(config.tipo, config)}
                      disabled={saving}
                      className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                      data-testid={`save-ncf-${config.tipo}`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Guardando...' : 'Guardar Configuración'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Tab: Configuración de Correo SMTP */}
        {isAdmin && (
        <TabsContent value="smtp">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                <Mail className="w-5 h-5 mr-2 text-cyan-500" />
                Configuración de Correo Electrónico
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  Configure el servidor SMTP para enviar comprobantes de pedidos por correo a los clientes.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Servidor SMTP (Host)</Label>
                  <Input
                    value={printConfig.smtp_host || ''}
                    onChange={(e) => setPrintConfig({ ...printConfig, smtp_host: e.target.value })}
                    placeholder="mail.tudominio.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Puerto</Label>
                  <Input
                    type="number"
                    value={printConfig.smtp_port || 465}
                    onChange={(e) => setPrintConfig({ ...printConfig, smtp_port: parseInt(e.target.value) })}
                    placeholder="465"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Usuario SMTP</Label>
                  <Input
                    value={printConfig.smtp_user || ''}
                    onChange={(e) => setPrintConfig({ ...printConfig, smtp_user: e.target.value })}
                    placeholder="correo@tudominio.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contraseña SMTP</Label>
                  <Input
                    type="password"
                    value={printConfig.smtp_password || ''}
                    onChange={(e) => setPrintConfig({ ...printConfig, smtp_password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Remitente para clientes (From)</Label>
                  <Input
                    value={printConfig.smtp_from || ''}
                    onChange={(e) => setPrintConfig({ ...printConfig, smtp_from: e.target.value })}
                    placeholder="pedidos@tudominio.com"
                  />
                  <p className="text-xs text-slate-500">Se usa en comprobantes enviados al cliente.</p>
                </div>
                <div className="space-y-2">
                  <Label>Remitente interno (From)</Label>
                  <Input
                    value={printConfig.smtp_from_internal || ''}
                    onChange={(e) => setPrintConfig({ ...printConfig, smtp_from_internal: e.target.value })}
                    placeholder="sistema@tudominio.com"
                  />
                  <p className="text-xs text-slate-500">Se usa en notificaciones a admin, taller y vendedor.</p>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Conexión</Label>
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="text-sm font-medium">Usar SSL/TLS</p>
                      <p className="text-xs text-slate-500">Puerto 465 usa SSL. Puerto 587 usa STARTTLS (desactivar).</p>
                    </div>
                    <Switch
                      checked={printConfig.smtp_ssl ?? true}
                      onCheckedChange={(checked) => setPrintConfig({ ...printConfig, smtp_ssl: checked })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSavePrint}
                  disabled={saving}
                  className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Tab: Categorías */}
        {isAdmin && (
        <TabsContent value="categories">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Tag className="w-5 h-5 text-cyan-500" />
                Categorías de productos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">
                Las categorías se usan al crear productos y aparecen en los reportes de ventas.
              </p>

              {/* Lista de categorías */}
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {categories.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-6">No hay categorías. Agrega la primera.</p>
                )}
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50">
                    {editingCat?.id === cat.id ? (
                      <>
                        <Input
                          value={editingCat.name}
                          onChange={e => setEditingCat({ ...editingCat, name: e.target.value })}
                          onKeyDown={async e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!editingCat.name.trim()) return;
                              setSavingCat(true);
                              try {
                                const updated = await updateCategory(cat.id, editingCat.name);
                                setCategories(prev => prev.map(c => c.id === cat.id ? updated : c));
                                setEditingCat(null);
                                toast.success('Categoría actualizada');
                              } catch (err) {
                                toast.error(err.response?.data?.error || 'Error al actualizar');
                              } finally { setSavingCat(false); }
                            }
                            if (e.key === 'Escape') setEditingCat(null);
                          }}
                          className="flex-1 h-8 text-sm"
                          autoFocus
                        />
                        <button disabled={savingCat} onClick={async () => {
                          if (!editingCat.name.trim()) return;
                          setSavingCat(true);
                          try {
                            const updated = await updateCategory(cat.id, editingCat.name);
                            setCategories(prev => prev.map(c => c.id === cat.id ? updated : c));
                            setEditingCat(null);
                            toast.success('Categoría actualizada');
                          } catch (err) {
                            toast.error(err.response?.data?.error || 'Error al actualizar');
                          } finally { setSavingCat(false); }
                        }} className="text-green-500 hover:text-green-700 p-1">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingCat(null)} className="text-slate-400 hover:text-slate-600 p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-slate-700">{cat.name}</span>
                        <button onClick={() => setEditingCat({ id: cat.id, name: cat.name })}
                          className="text-slate-400 hover:text-cyan-500 p-1 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={async () => {
                          try {
                            const res = await deleteCategory(cat.id);
                            setCategories(prev => prev.filter(c => c.id !== cat.id));
                            if (res.products_affected > 0) {
                              toast.warning(`Categoría eliminada. ${res.products_affected} producto(s) quedan sin categoría asignada.`);
                            } else {
                              toast.success('Categoría eliminada');
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.error || 'Error al eliminar');
                          }
                        }} className="text-slate-400 hover:text-rose-500 p-1 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Agregar nueva */}
              {addingCat ? (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Nombre de la categoría"
                    value={newCatName}
                    onChange={e => setNewCatName(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!newCatName.trim()) return;
                        setSavingCat(true);
                        try {
                          const cat = await createCategory(newCatName);
                          setCategories(prev => [...prev, cat]);
                          setNewCatName('');
                          setAddingCat(false);
                          toast.success('Categoría creada');
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Error al crear');
                        } finally { setSavingCat(false); }
                      }
                      if (e.key === 'Escape') { setAddingCat(false); setNewCatName(''); }
                    }}
                    className="flex-1 text-sm"
                    autoFocus
                  />
                  <Button size="sm" disabled={savingCat || !newCatName.trim()}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    onClick={async () => {
                      if (!newCatName.trim()) return;
                      setSavingCat(true);
                      try {
                        const cat = await createCategory(newCatName);
                        setCategories(prev => [...prev, cat]);
                        setNewCatName('');
                        setAddingCat(false);
                        toast.success('Categoría creada');
                      } catch (err) {
                        toast.error(err.response?.data?.error || 'Error al crear');
                      } finally { setSavingCat(false); }
                    }}>
                    {savingCat ? 'Guardando...' : 'Guardar'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setAddingCat(false); setNewCatName(''); }}>
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setAddingCat(true)}
                  className="text-cyan-600 border-cyan-200 hover:bg-cyan-50">
                  <Plus className="w-4 h-4 mr-2" /> Agregar categoría
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Tab: Mis Preferencias */}
        <TabsContent value="preferences">
          <div className="space-y-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                <User className="w-5 h-5 mr-2 text-cyan-500" />
                Mi cuenta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-cyan-500 rounded-full flex items-center justify-center font-bold text-white text-xl shrink-0">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="text-lg font-semibold">{user?.name}</p>
                  <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {user?.email && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-slate-400 mb-0.5">Correo</p>
                    <p className="font-medium text-sm">{user.email}</p>
                  </div>
                )}
                {user?.branch_name && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-slate-400 mb-0.5">Sucursal</p>
                    <p className="font-medium text-sm">{user.branch_name}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                <User className="w-5 h-5 mr-2 text-cyan-500" />
                Mis Preferencias de Comprobante
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  Esta configuración aplica solo a tu usuario. Define cómo se entregará el comprobante al cliente después de cada pago.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Método predeterminado</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'print',  icon: '🖨️', label: 'Siempre imprimir',  desc: 'Descarga el PDF automáticamente' },
                    { value: 'email',  icon: '📧', label: 'Siempre por correo', desc: 'Envía al correo del cliente automáticamente' },
                    { value: 'ask',    icon: '❓', label: 'Preguntar siempre',  desc: 'Muestra opciones después de cada pago' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPrefConfig({ ...prefConfig, receipt_preference: opt.value })}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                        prefConfig.receipt_preference === opt.value
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <span className="text-2xl">{opt.icon}</span>
                      <span className="font-semibold text-sm">{opt.label}</span>
                      <span className="text-xs text-slate-500">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {(prefConfig.receipt_preference === 'email' || prefConfig.receipt_preference === 'ask') && (
                <div className="space-y-2">
                  <Label className="font-medium">Tiempo de espera del temporizador</Label>
                  <p className="text-xs text-slate-500">
                    Segundos antes de ejecutar la acción predeterminada automáticamente. El usuario puede interactuar para pausarlo.
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min={5}
                      max={60}
                      step={5}
                      value={prefConfig.receipt_timer}
                      onChange={(e) => setPrefConfig({ ...prefConfig, receipt_timer: Number(e.target.value) })}
                      className="flex-1 accent-cyan-500"
                    />
                    <span className="w-16 text-center font-semibold text-slate-700 bg-slate-100 rounded-lg py-1">
                      {prefConfig.receipt_timer}s
                    </span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleSavePreferences}
                disabled={savingPref}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingPref ? 'Guardando...' : 'Guardar preferencias'}
              </Button>

              {/* Change password — collapsible */}
              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(p => !p)}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPasswordForm ? '▲ Ocultar cambio de contraseña' : '🔑 Cambiar contraseña'}
                </button>
                {showPasswordForm && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Contraseña actual</Label>
                        <Input type="password" value={passwordForm.current_password}
                          onChange={e => setPasswordForm({...passwordForm, current_password: e.target.value})}
                          placeholder="••••••••" />
                      </div>
                      <div className="space-y-2">
                        <Label>Nueva contraseña</Label>
                        <Input type="password" value={passwordForm.new_password}
                          onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})}
                          placeholder="••••••••" />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirmar contraseña</Label>
                        <Input type="password" value={passwordForm.confirm_password}
                          onChange={e => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                          placeholder="••••••••" />
                      </div>
                    </div>
                    <Button onClick={handleChangePassword} disabled={savingPassword}
                      className="bg-slate-700 hover:bg-slate-800 text-white">
                      <Save className="w-4 h-4 mr-2" />
                      {savingPassword ? 'Guardando...' : 'Cambiar contraseña'}
                    </Button>
                  </div>
                )}
              </div>

            </CardContent>
          </Card>
          </div>
        </TabsContent>

        {/* Tab: Notificaciones */}
        {isAdmin && (
        <TabsContent value="notifications">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                <Bell className="w-5 h-5 mr-2 text-cyan-500" />
                Configuración de Notificaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  Define qué correos se envían automáticamente cuando cambia el estado de un pedido de confección.
                </p>
              </div>

              <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                <div>
                  <p className="font-medium text-sm">Activar notificaciones por correo</p>
                  <p className="text-xs text-slate-500">Desactiva para suspender todos los envíos automáticos</p>
                </div>
                <Switch
                  checked={notifConfig.notifications_enabled}
                  onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notifications_enabled: v })}
                />
              </div>

              <div className={`space-y-6 ${!notifConfig.notifications_enabled ? 'opacity-40 pointer-events-none' : ''}`}>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">🔵 Al pasar a Pre-producción</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Correo Administrador</Label>
                        <Switch checked={notifConfig.notify_admin_pre_produccion}
                          onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notify_admin_pre_produccion: v })} />
                      </div>
                      <Input type="email" value={notifConfig.admin_email}
                        onChange={(e) => setNotifConfig({ ...notifConfig, admin_email: e.target.value })}
                        placeholder="admin@empresa.com" disabled={!notifConfig.notify_admin_pre_produccion} />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Correo Taller</Label>
                        <Switch checked={notifConfig.notify_taller_pre_produccion}
                          onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notify_taller_pre_produccion: v })} />
                      </div>
                      <Input type="email" value={notifConfig.taller_email}
                        onChange={(e) => setNotifConfig({ ...notifConfig, taller_email: e.target.value })}
                        placeholder="taller@empresa.com" disabled={!notifConfig.notify_taller_pre_produccion} />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">🟣 Al pasar a Confeccionado</h3>
                  <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">Notificar al vendedor</p>
                      <p className="text-xs text-slate-500">Se usa el correo del usuario vendedor</p>
                    </div>
                    <Switch checked={notifConfig.notify_seller_listo}
                      onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notify_seller_listo: v })} />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">🟢 Al pasar a Listo para entrega</h3>
                  <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">Notificar al cliente</p>
                      <p className="text-xs text-slate-500">Se usa el correo del perfil del cliente</p>
                    </div>
                    <Switch checked={notifConfig.notify_client_listo}
                      onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notify_client_listo: v })} />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">🏦 Turnos de caja</h3>
                  <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">Notificar al abrir turno</p>
                      <p className="text-xs text-slate-500">Envía al admin un correo con pedidos vencidos al abrir caja</p>
                    </div>
                    <Switch checked={notifConfig.notify_cash_open ?? true}
                      onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notify_cash_open: v })} />
                  </div>
                  <div className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">Notificar al cerrar turno</p>
                      <p className="text-xs text-slate-500">Envía al admin el resumen de ventas y pedidos vencidos al cerrar caja</p>
                    </div>
                    <Switch checked={notifConfig.notify_cash_close ?? true}
                      onCheckedChange={(v) => setNotifConfig({ ...notifConfig, notify_cash_close: v })} />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveNotifications} disabled={savingNotif}
                className="bg-cyan-500 hover:bg-cyan-600 text-white">
                <Save className="w-4 h-4 mr-2" />
                {savingNotif ? 'Guardando...' : 'Guardar configuración'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

      </Tabs>
    </div>
  );
};

export default Settings;